import React from 'react';
import { Box, Avatar, Typography, IconButton, Tooltip, Checkbox, Link } from '@mui/material';
import LinkIcon from "@mui/icons-material/Link";
import { IEquipmentServiceTobeScheduled  } from '../../../../store/reducers/scheduledServices';
import { i18n } from '../../../../translate/i18n';
import moment from 'moment';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../../store/types';
import * as actions from '../../../../store/actions';
import { EServiceStatus } from '../Modals/ServicesForSchedulingFiltersModal';
import Chip from '../../../../components/Chip';

type Props = {
  equipment: IEquipmentServiceTobeScheduled,
  goToEquipmentPage: (equipmentId: string) => void,
  disabled?: boolean,
}


const EventCard: React.FC<Props> = ({ equipment, goToEquipmentPage, disabled = false }) => {
  const dispatch = useDispatch();
  const selectedServices = useSelector((state: RootState) => state.scheduledServices.selectedServicesToBeScheduled);

  const onDragStartHandle = (event: React.DragEvent<HTMLDivElement>, equipment: IEquipmentServiceTobeScheduled) => {
    const dataToBeTransfered = JSON.stringify(selectedServices);
    event.dataTransfer.setData('cardInfo', dataToBeTransfered);
  }

  const isTodayAfterExpirationDate = moment().isAfter(equipment.scheduledServicesReplicated.expirationDate);

  const isCardSelected = selectedServices.some((equipWithService) => (equipWithService._id === equipment._id && equipWithService.scheduledServicesReplicated.equipmentServiceId._id === equipment.scheduledServicesReplicated.equipmentServiceId._id));

  const noServiceDone = !equipment.equipmentServicesReplicate.some((serviceReplicate) => serviceReplicate.equipmentServiceId === equipment.scheduledServicesReplicated.equipmentServiceId._id);
  const hasExpirationDateAndIsOverdue = equipment.scheduledServicesReplicated.expirationDate && isTodayAfterExpirationDate;

  const getServiceStatus = (): EServiceStatus => {

    let status = EServiceStatus.noService;

    if (noServiceDone) {
      status = EServiceStatus.noService;
    } else if (hasExpirationDateAndIsOverdue) {
      status = EServiceStatus.overdue;
    } else {
      status = EServiceStatus.valid;
    }

    return status;
  };

  const getStatusColors = (status: EServiceStatus) => {
    const colorsObject = {
      valid: {
        color: 'rgba(2, 136, 209, 1)',
        backgroundColor: 'rgba(2, 136, 209, 0.3)',
      },
      noService: {
        color: 'rgba(239, 108, 0, 1)',
        backgroundColor: 'rgba(239, 108, 0, 0.3)',
      },
      overdue: {
        color: 'rgba(211, 47, 47, 1)',
        backgroundColor: 'rgba(211, 47, 47, 0.3)',
      },
    }
    return colorsObject[status];
  }

  const serviceStatus = getServiceStatus();
  const statusColors = getStatusColors(serviceStatus);

  return (
    <Box
      draggable={selectedServices.length > 0 && isCardSelected && !disabled}
      sx={{
        ...styles.cardContainer,
        borderLeftColor: isTodayAfterExpirationDate ? 'red' : '#0288D1',
        backgroundColor: isCardSelected ? 'rgba(76, 175, 80, 0.2)' : 'white',
        boxShadow: isCardSelected ? '' : '0.3em 0em 0.3em rgba(0, 0, 0, 0.20)',
      }}
      onDragStart={(event) => onDragStartHandle(event, equipment)}
      overflow='ellipsis'
      onClick={() => {
        if (disabled) return;
        if (isCardSelected) dispatch(actions.scheduledServicesRemoveSelectedServiceToBeScheduled(equipment));
        else dispatch(actions.scheduledServicesAddSelectedServiceToBeScheduled(equipment));
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%'}}>
        <Chip
          backgroundColor={statusColors.backgroundColor}
          color={statusColors.color}
          label={ i18n.t(`scheduledServicesServicesForSchedulingFilterModal.${serviceStatus}`) as string}
        />
        <Checkbox
          checked={isCardSelected}
          disableRipple
          sx={{
            padding: 0,
            '&:hover': {
              backgroundColor: 'transparent !important'
            }
          }}
          disabled={disabled}
        />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', width: '100%'}}>
        {equipment.photo?.url ?
          <Avatar src={equipment.photo?.url} sx={styles.avatar} />
          :
          <Avatar sx={styles.avatarSmallText}>
            PHOTO
          </Avatar>
        }
        <Box width={'75%'} sx={{ marginLeft: '5px'}}>
          <Typography variant='subtitle1' fontWeight={600} noWrap width='100%' sx={{fontSize: 16}}>{equipment.equipmentModel.name}</Typography>
          <Typography
            variant='subtitle1'
            noWrap
            width='100%'
            sx={{
              color: 'rgba(0, 0, 0, 0.38)',
              fontSize: 12,
              fontWeight: '400',
              letterSpacing: 0.40
            }}
          >
            {equipment.serialNumber}
          </Typography>
        </Box>
        <IconButton
          sx={{ marginLeft: '5px', cursor: 'pointer', padding: '0px 3px 0px 3px', borderRadius: 3 }}
          onClick={(e) => {
            e.stopPropagation();
          }}
          size="large"
          href={`/form-equip/${equipment._id}/scheduled-services-planner`}
          target="_blank"
        >
          <Tooltip title={i18n.t('editService.goToEquipment') as string}>
            <LinkIcon color='primary' />
          </Tooltip>
        </IconButton>
      </Box>
      <Box sx={styles.cardInfoContainer}>
        <Typography variant='caption'>
          <span style={{color: 'rgba(0, 0, 0, 0.38)'}}>
            {i18n.t('scheduledServicesDraggableCard.service') as string}
          </span>
          <span style={{fontWeight: 700}}>
            {equipment.scheduledServicesReplicated.equipmentServiceName}
          </span>
        </Typography>
        <Typography variant='caption' sx={styles.cardText}>
          <span style={{color: 'rgba(0, 0, 0, 0.38)'}}>
            {i18n.t('scheduledServicesDraggableCard.company') as string}
          </span>
          {equipment.companies.map(company => company.name).join(', ')}
        </Typography>
        <Typography variant='caption' sx={styles.cardText}>
          <span style={{color: 'rgba(0, 0, 0, 0.38)'}}>
            {i18n.t('scheduledServicesDraggableCard.place') as string}
          </span>
          {equipment.place.path.length > 40 ? `...${equipment.place.path.slice(-40)}` : equipment.place.path}
        </Typography>
        {equipment.scheduledServicesReplicated.expirationDate && <Typography variant='caption' sx={styles.cardText}>
          <span style={{color: 'rgba(0, 0, 0, 0.38)'}}>
            {i18n.t('scheduledServicesDraggableCard.expirationDate') as string}
          </span>
          {new Date(equipment.scheduledServicesReplicated.expirationDate).toLocaleDateString('pt-BR')}
        </Typography>}
      </Box>
    </Box>
  )
};

export default EventCard;


const styles = {
  cardContainer: {
    flexDirection: 'column',
    width: '100%',
    padding: '16px 12px',
    background: 'white',
    boxShadow: '0px 2px 1px 0px rgba(0, 0, 0, 0.20)',
    borderRadius: '4px',
    borderLeft: '6px #0288D1 solid',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: '10px',
    display: 'flex',
    maxWidth: '26rem',
    cursor: 'pointer',
  },
  avatarSmallText: {
    fontSize: '0.75rem',
    width: '3rem',
    height: '3rem',
  },
  avatar: {
    width: '3rem',
    height: '3rem',
  },
  cardInfoContainer: {
    display: 'flex',
    flexDirection: 'column',
    width:'100%',
  },
  cardText: {
    color: 'black',
  }
}
